import React, { createContext, useContext, useRef, useState } from 'react'
import { Spin } from 'antd'
import styled from 'styled-components'
import i18n from 'src/helpers/i18n'

// Estilos para el spinner
const LoadingContainer = styled.div`
   position: fixed;
   top: 0;
   left: 0;
   right: 0;
   bottom: 0;
   background-color: #ffffff60;
   display: flex;
   justify-content: center;
   align-items: center;
   z-index: 9999;
`

// Definimos la interfaz para el contexto
interface LoadingContextType {
   showLoading: () => void
   hideLoading: () => void
}

// Creamos el contexto
export const LoadingContext = createContext<LoadingContextType | null>(null)

// Hook personalizado para usar el loading
export const useLoading = (): LoadingContextType => {
   const context = useContext(LoadingContext)
   if (!context) {
      throw new Error('useLoading debe ser usado dentro de un LoadingProvider')
   }
   return context
}

// Componente que provee el contexto de loading
export const LoadingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
   const [visible, setVisible] = useState(false)

   const showLoading = () => setVisible(true)
   const hideLoading = () => setVisible(false)

   return (
      <LoadingContext.Provider value={{ showLoading, hideLoading }}>
         <Spin spinning={visible} tip={i18n.t('loading')}>
            {children}
         </Spin>
      </LoadingContext.Provider>
   )
}

// Método alternativo usando una referencia directa al componente
export const createLoadingRef = () => {
   let showLoading: () => void = () => {}
   let hideLoading: () => void = () => {}

   const LoadingComponent = () => {
      const [visible, setVisible] = useState(false)

      // Asignamos las funciones a las variables externas
      showLoading = () => setVisible(true)
      hideLoading = () => setVisible(false)

      // Solo renderizamos si es visible
      if (!visible) return null

      return (
         <LoadingContainer>
            <Spin spinning={visible} tip={i18n.t('loading')} />
         </LoadingContainer>
      )
   }

   return {
      LoadingComponent,
      showLoading: () => showLoading(),
      hideLoading: () => hideLoading()
   }
}
